import { useState } from 'react';
import cn from 'classnames';
import { Form, OverlayTrigger, Popover } from 'react-bootstrap';
import { MdFilterList } from 'react-icons/md';
import { InteractiveSpan } from '@Components/InteractiveEl';
import { searchItems } from '@src/utils/search';
import './ColumnFilterPopover.scss';

// A facet's selection is normally "which values are included" (empty = no filter, show all).
// That representation has no way to mean "explicitly show nothing for this facet" -- an empty
// Set already means "show everything" -- so a "None" quick-action needs a distinct marker.
// Embeds a newline, which no real filterable value is expected to contain, so it can never
// collide with real data.
export const NONE_SELECTED = '\n__none__\n';

interface ColumnFilterPopoverProps {
  label: string;
  options: { value: string; count: number }[];
  selected: ReadonlySet<string>;
  colorMap?: Record<string, string>;
  onChange: (next: ReadonlySet<string>) => void;
}

// Generic column-header filter popover: a search box that narrows the option list, a checkbox
// pick-list (unchecked by default -- this is an explicit allow-list, not an "everything on,
// uncheck to exclude" list), and All/None quick actions along the bottom.
export const ColumnFilterPopover = ({
  label,
  options,
  selected,
  colorMap,
  onChange
}: ColumnFilterPopoverProps) => {
  const [show, setShow] = useState(false);
  const [query, setQuery] = useState('');

  const visibleOptions = searchItems(options, query, { keys: ['value'], rank: false });

  const toggle = (value: string) => {
    const next = new Set(selected);
    next.delete(NONE_SELECTED);
    if (next.has(value)) {
      next.delete(value);
    } else {
      next.add(value);
    }
    onChange(next);
  };

  const selectAll = () => {
    onChange(new Set(options.map(o => o.value)));
    setShow(false);
  };

  const selectNone = () => {
    onChange(new Set([NONE_SELECTED]));
    setShow(false);
  };

  const popover = (
    <Popover className='column-filter-popover' style={{ minWidth: 240, maxWidth: 320 }}>
      <Popover.Body>
        <Form.Control
          className='column-filter-search'
          type='search'
          size='sm'
          placeholder={`Search ${label.toLowerCase()}...`}
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
        <div className='column-filter-options'>
          {visibleOptions.map(({ value, count }) => (
            <label
              key={value}
              className='column-filter-option'
              htmlFor={`filter-${label}-${value}`}
            >
              <Form.Check.Input
                id={`filter-${label}-${value}`}
                type='checkbox'
                checked={selected.has(value)}
                onChange={() => toggle(value)}
                style={{ width: 14, height: 14, marginTop: 0 }}
              />
              {colorMap && (
                <span
                  className='column-filter-option-dot'
                  style={{ backgroundColor: colorMap[value] }}
                />
              )}
              <span className='column-filter-option-name'>{value}</span>
              <span className='column-filter-option-count'>{count}</span>
            </label>
          ))}
        </div>
        <div className='column-filter-quick-actions'>
          <InteractiveSpan className='column-filter-quick-action' onClick={selectAll}>
            All
          </InteractiveSpan>
          <InteractiveSpan className='column-filter-quick-action' onClick={selectNone}>
            None
          </InteractiveSpan>
        </div>
      </Popover.Body>
    </Popover>
  );

  const isNone = selected.has(NONE_SELECTED);
  const triggerLabel = () => {
    if (isNone) return 'None';
    if (selected.size === 0) return 'All';
    return `${selected.size} selected`;
  };

  return (
    <OverlayTrigger
      trigger='click'
      placement='bottom-start'
      overlay={popover}
      show={show}
      onToggle={setShow}
      rootClose
    >
      <InteractiveSpan className={cn('column-filter-trigger', { 'is-active': selected.size > 0 })}>
        <MdFilterList />
        {triggerLabel()}
      </InteractiveSpan>
    </OverlayTrigger>
  );
};
